"use client";

import Link from "next/link";
import AppLogo from "@/components/ui/AppLogo";
import Icon from "@/components/ui/AppIcon";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";

const footerLinks = {
  Company: [
    { label: "About", href: "/about" },
    { label: "How It Works", href: "/how-it-works" },
    { label: "Contact", href: "/contact" },
  ],
  Services: [
    { label: "Sourcing", href: "/services" },
    { label: "Warehousing", href: "/services" },
    { label: "Shipping", href: "/services" },
    { label: "Integrations", href: "/services" },
  ],
  Resources: [
    { label: "Pricing", href: "/pricing" },
    { label: "Book a Call", href: "/contact" },
  ],
};

const socials = [
  { label: "LinkedIn", icon: "GlobeAltIcon", href: "#" },
  { label: "Twitter", icon: "ChatBubbleLeftIcon", href: "#" },
];

export default function Footer() {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <footer className="bg-secondary-dark border-t border-divider" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        {/* Top */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-5 gap-12 mb-14"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {/* Brand */}
          <motion.div className="col-span-2 space-y-5" variants={itemVariants}>
            <AppLogo text="Helium" iconName="CubeIcon" size={64} />
            <p className="text-sm text-text-secondary leading-relaxed max-w-xs">
              Factory-speed China fulfilment with European communication standards. Your brand’s logistics partner — from Shenzhen to your customer’s door.
            </p>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent-primary bg-glass-white px-3 py-1.5 rounded-full border border-accent-primary/20">
                <span className="w-1.5 h-1.5 rounded-full bg-accent-primary animate-glow-pulse" />
                Shenzhen, China
              </span>
            </div>
          </motion.div>

          {/* Link Groups */}
          {Object.entries(footerLinks)?.map(([group, links]) => (
            <motion.div key={group} className="space-y-4" variants={itemVariants}>
              <h4 className="text-xs font-bold tracking-widest uppercase text-text-secondary font-heading">
                {group}
              </h4>
              <ul className="space-y-2.5">
                {links?.map((link) => (
                  <li key={link?.label}>
                    <Link
                      href={link?.href}
                      className="text-sm text-text-secondary hover:text-accent-primary transition-colors font-medium"
                    >
                      {link?.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom */}
        <motion.div 
          className="flex flex-col md:flex-row items-center justify-between gap-6 pt-8 border-t border-divider"
          variants={itemVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <p className="text-xs text-text-secondary">
            © {new Date()?.getFullYear()} Helium Fulfilment. European-operated. Shenzhen-based. Tech-driven.
          </p>
          <div className="flex items-center gap-4">
            {socials?.map((social) => (
              <Link
                key={social?.label}
                href={social?.href}
                className="w-9 h-9 rounded-xl bg-glass-white border border-glass-border flex items-center justify-center text-text-secondary hover:text-accent-primary hover:border-accent-primary/30 transition-all"
                aria-label={social?.label}
              >
                <Icon name={social?.icon} size={16} />
              </Link>
            ))}
          </div>
        </motion.div>
      </div>
    </footer>
  );
}